/**
 * SelectionMode — §5.6's two modes on iOS, and the shared stylesheet for the sentence menu.
 *
 * ── Why there are two modes at all ──────────────────────────────────────────
 *
 * The native long-press callout and a custom long-press menu are **mutually exclusive**, and the
 * callout's timing is not ours to race. WebKit begins its own selection UI on a touch-hold at a
 * moment we cannot observe and cannot cancel from JavaScript; a custom handler that fires "at about
 * the same time" produces both, or neither, depending on how fast the finger was. So the reader
 * declares which one it wants:
 *
 *   **Reading mode** (default) — `-webkit-touch-callout: none; user-select: none` on the track. The
 *   callout never appears, and a 450 ms hold opens our menu (see `SentenceMenu`).
 *
 *   **Selection mode** — the two properties come off and iOS's own selection handles come back, for
 *   30 seconds, with a "Done selecting" chip that carries a **Regenerate** button. Entered from the
 *   menu's "Select text" row or by double-tap.
 *
 * ⚠️ **Only on coarse pointers.** On a desktop the native context menu and native text selection are
 * both wanted and both work, and Karti's own words for this feature were *"right-click on a selected
 * sentence"* — a reader that disabled `user-select` everywhere would have made the case he described
 * impossible to reach. The rule is expressed as `@media (pointer: coarse)` in the stylesheet rather
 * than as a `navigator.userAgent` test, because a UA sniff is wrong on an iPad with a trackpad and on
 * every device that has not shipped yet.
 *
 * ── The 30 seconds ──────────────────────────────────────────────────────────
 *
 * Selection mode is a *state the reader is stuck in* until something ends it, and the thing that most
 * often ends it is nothing: the user selects, regenerates, and puts the phone down. Leaving native
 * selection on means the next long-press anywhere in the book pops the iOS callout instead of the
 * menu, and the reader appears to have lost a feature. The timer is reset by every interaction inside
 * the mode, so it expires on **inactivity**, never under someone's finger.
 */

import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";

import { compareAddress } from "../../../shared/address.ts";

/** §5.6. Long enough to select a paragraph two-handed; short enough that a forgotten mode self-heals. */
export const SELECTION_MODE_MS = 30_000;

// ─── which sentences a selection touches ─────────────────────────────────────

/**
 * Every `[data-s]` the current selection intersects, in reading order.
 *
 * ⚠️ **`compareAddress` is the only orderer** (AGENTS.md). Not `localeCompare`, not DOM order, not a
 * numeric sort on a parsed suffix: DOM order and reading order agree in the reader today and would
 * stop agreeing the first time a floated figure or a footnote moved in the markup, and the failure
 * would be a regeneration applied to the right sentences in the wrong order — which nothing detects,
 * because every one of them is a legal sid.
 *
 * A collapsed selection (a caret, not a range) returns nothing. `Range.intersectsNode` answers true
 * for a caret sitting against a node's boundary, so without this a single tap in the margin would
 * silently target a sentence the user never highlighted.
 */
export function sidsInSelection(track: HTMLElement | null, selection: Selection | null = typeof window === "undefined" ? null : window.getSelection()): string[] {
  if (track === null || selection === null || selection.rangeCount === 0 || selection.isCollapsed) return [];

  const found = new Set<string>();
  for (let i = 0; i < selection.rangeCount; i++) {
    const range = selection.getRangeAt(i);
    if (range.collapsed) continue;
    for (const el of track.querySelectorAll<HTMLElement>("[data-s]")) {
      const sid = el.getAttribute("data-s");
      if (sid === null || sid === "") continue;
      if (range.intersectsNode(el)) found.add(sid);
    }
  }
  return [...found].sort(compareAddress);
}

/** The `[data-s]` an event landed on, or null. `closest` because the span wraps inline children. */
export function sidAt(target: EventTarget | null): string | null {
  if (!(target instanceof Element)) return null;
  const el = target.closest<HTMLElement>("[data-s]");
  const sid = el?.getAttribute("data-s") ?? null;
  return sid === "" ? null : sid;
}

// ─── the mode ────────────────────────────────────────────────────────────────

export interface SelectionModeState {
  /** True while native selection is restored. The long-press opener must stand down while it is. */
  active: boolean;
  /** Whole seconds left, for the chip's countdown. 0 when inactive. */
  remaining: number;
  /** The sids currently selected, live. Empty until the user has actually dragged handles. */
  sids: string[];
  enter(): void;
  exit(): void;
}

/**
 * Reading mode ⇄ selection mode, as a `data-` attribute on the track and a self-cancelling timer.
 *
 * The attribute rather than inline styles: the two properties this toggles are only correct on a
 * coarse pointer, and a media query can say that where a style object cannot. Inline styles would
 * also have to be removed by exactly the code that set them, which is the shape of bug that leaves a
 * reader permanently unable to select text after one unmount.
 */
export function useSelectionMode(track: HTMLElement | null): SelectionModeState {
  const [active, setActive] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [sids, setSids] = useState<string[]>([]);
  const deadline = useRef(0);

  const exit = useCallback(() => {
    deadline.current = 0;
    setActive(false);
    setRemaining(0);
    setSids([]);
    if (typeof window !== "undefined") window.getSelection()?.removeAllRanges();
  }, []);

  const enter = useCallback(() => {
    deadline.current = Date.now() + SELECTION_MODE_MS;
    setActive(true);
    setRemaining(Math.ceil(SELECTION_MODE_MS / 1000));
  }, []);

  // The attribute lives on the track and is removed on unmount, so a reader that navigates away
  // mid-selection does not leave the next book in a mode nobody asked for.
  useEffect(() => {
    if (track === null) return;
    track.setAttribute("data-ab-select", active ? "on" : "off");
    return () => track.removeAttribute("data-ab-select");
  }, [track, active]);

  useEffect(() => {
    if (!active) return;

    const tick = () => {
      const left = deadline.current - Date.now();
      if (left <= 0) {
        exit();
        return;
      }
      setRemaining(Math.ceil(left / 1000));
      setSids(sidsInSelection(track));
    };

    // Every interaction inside the mode pushes the deadline out: it expires on inactivity, never
    // under someone's finger. `selectionchange` is the only event that fires while iOS's own handles
    // are being dragged — a `pointermove` listener never sees it.
    const bump = () => {
      deadline.current = Date.now() + SELECTION_MODE_MS;
      setSids(sidsInSelection(track));
    };

    const id = window.setInterval(tick, 500);
    document.addEventListener("selectionchange", bump);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("selectionchange", bump);
    };
  }, [active, track, exit]);

  return { active, remaining, sids, enter, exit };
}

// ─── the chip ────────────────────────────────────────────────────────────────

export interface SelectionChipProps {
  mode: SelectionModeState;
  /**
   * Present only when this actor may regenerate, computed from `shared/access.ts` by the caller.
   * **LAW 13**: absent means the predicate said no, never that a flag was off — so the button is
   * missing rather than present and dead, and there is nothing here for a stale `disabled` prop to
   * lie about.
   */
  onRegenerate?: ((sids: string[]) => void) | null;
  /** "Sounds wrong" on a selection. Everyone, always, zero GPU. */
  onReport?: ((sids: string[]) => void) | null;
}

/**
 * The "Done selecting" chip. Fixed to the bottom of the viewport, inside the safe area, because on a
 * phone the top of the screen is where iOS puts its own selection callout and the two must not fight.
 */
export function SelectionChip(props: SelectionChipProps): ReactElement | null {
  const { mode, onRegenerate, onReport } = props;
  useEffect(injectReaderMenuCss, []);
  if (!mode.active) return null;

  const n = mode.sids.length;
  return (
    <div className="ab-sel-chip" role="status">
      <span className="ab-sel-count">
        {n === 0 ? "Select some text" : `${n} sentence${n === 1 ? "" : "s"}`}
        <span className="ab-sel-timer"> · {mode.remaining}s</span>
      </span>
      {onReport && n > 0 ? (
        <button type="button" className="ab-sel-btn" onClick={() => onReport(mode.sids)}>
          Sounds wrong
        </button>
      ) : null}
      {onRegenerate && n > 0 ? (
        <button type="button" className="ab-sel-btn ab-sel-primary" onClick={() => onRegenerate(mode.sids)}>
          Regenerate {n}
        </button>
      ) : null}
      <button type="button" className="ab-sel-btn" onClick={mode.exit}>
        Done selecting
      </button>
    </div>
  );
}

// ─── the stylesheet ──────────────────────────────────────────────────────────

const STYLE_ID = "ab-sentence-menu-css";

/**
 * One `<style>` element, injected once, shared by `SentenceMenu`, `SelectionMode` and `TakePicker`.
 *
 * ⚠️ It is here — in code — rather than in `client/src/ui/reader.css` for one reason only: that file
 * and `tokens.css` are **lifted verbatim from books.karti.ai with a provenance header** and are owned
 * by another agent this phase. Every value below is a `var(--…)` from those files. **No hex is
 * hardcoded** (AGENTS.md), the reader's four themes therefore recolour this menu for free, and the
 * commit that folds this string into `reader.css` is a paste with the selectors unchanged.
 *
 * Idempotent, so three components mounting in any order install it exactly once.
 */
export function injectReaderMenuCss(): void {
  if (typeof document === "undefined" || document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

const CSS = `
/* ── reading mode vs selection mode ─────────────────────────────────────────
   Coarse pointers ONLY. On a desktop, native selection and the native context menu are both wanted:
   "right-click on a selected sentence" is the case this whole feature was asked for. */
@media (pointer: coarse) {
  [data-ab-select="off"] {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
  }
  [data-ab-select="on"] {
    -webkit-touch-callout: default;
    -webkit-user-select: text;
    user-select: text;
  }
}

/* The 90 ms pulse that acknowledges a long press. No haptic: navigator.vibrate does not exist on iOS
   Safari, and shipping one that only fires on Android is not "done". */
[data-s][data-ab-press="1"] {
  transition: transform 90ms var(--ease-out, ease-out);
  transform: scale(0.985);
  transform-origin: center;
}
@media (prefers-reduced-motion: reduce) {
  [data-s][data-ab-press="1"] { transition: none; transform: none; background: var(--read-narrating); }
}

/* ── the menu ───────────────────────────────────────────────────────────────── */
.ab-sm-scrim {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: transparent;
}
.ab-sm {
  position: fixed;
  z-index: 61;
  min-width: 15rem;
  max-width: min(20rem, calc(100vw - 2 * var(--space-3)));
  padding: var(--space-1) 0;
  font-family: var(--font-ui);
  font-size: var(--text-sm, 0.875rem);
  color: var(--read-ink);
  background: var(--read-bg);
  border: 1px solid var(--read-rule);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lift);
  overflow: hidden auto;
  max-height: min(70vh, 32rem);
}
/* ⚠️ The container is focused programmatically when the menu opens (so Escape and the arrow-key
   swallow work), and app.css's global \`:focus-visible\` rule then drew a 2px RUBRIC outline around
   the entire panel — on a 390 px phone that is a bright orange box filling half the screen, and it
   fired on a long press, where no keyboard affordance is wanted at all. Observed in a real Chrome at
   390 px, dark. The keyboard affordance belongs on the ITEMS, which have their own ring below; the
   panel already reads as a panel through its border and lift. */
.ab-sm:focus,
.ab-sm:focus-visible { outline: none; }
.ab-sm-title-row {
  padding: var(--space-2) var(--space-3);
  color: var(--read-ink-muted);
  font-size: var(--text-xs, 0.75rem);
  letter-spacing: 0.02em;
  border-bottom: 1px solid var(--read-rule);
}
.ab-sm-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  width: 100%;
  min-height: 2.75rem; /* a phone tap target, not a desktop one */
  padding: var(--space-2) var(--space-3);
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: start;
  cursor: pointer;
}
.ab-sm-item:hover:not(:disabled) { background: var(--reader-hover, var(--hover)); }
.ab-sm-item:focus-visible { outline: 2px solid var(--ring); outline-offset: -2px; }
.ab-sm-item:disabled { opacity: 0.45; cursor: default; }
.ab-sm-item[data-danger="1"] { color: var(--destructive); }
.ab-sm-hint {
  flex: 0 0 auto;
  color: var(--read-ink-muted);
  font-size: var(--text-2xs, 0.6875rem);
  text-align: end;
  max-width: 9rem;
}
.ab-sm-sep { height: 1px; margin: var(--space-1) 0; background: var(--read-rule); border: 0; }

/* ── the panels the menu opens (pronunciation, takes) ───────────────────────── */
.ab-sm-panel {
  padding: var(--space-3);
  font-family: var(--font-ui);
  font-size: var(--text-sm, 0.875rem);
  color: var(--read-ink);
}
.ab-sm-head { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); }
.ab-sm-title { font-weight: 600; }
.ab-sm-x {
  border: 0; background: transparent; color: var(--read-ink-muted);
  font: inherit; cursor: pointer; padding: var(--space-1);
}
.ab-sm-quote {
  margin: var(--space-2) 0;
  font-family: var(--font-read);
  color: var(--read-ink);
  max-height: 6.5em;
  overflow: hidden auto;
}
.ab-sm-note { margin: var(--space-2) 0 0; color: var(--read-ink-muted); }
.ab-sm-quiet { font-size: var(--text-2xs, 0.6875rem); }
.ab-sm-bad { color: var(--destructive); }
.ab-sm-tts { font-family: var(--font-mono); font-size: 0.9em; }
.ab-sm-input {
  width: 100%;
  margin-top: var(--space-2);
  padding: var(--space-2);
  font: inherit;
  color: var(--read-ink);
  background: var(--read-bg);
  border: 1px solid var(--rule-interactive);
  border-radius: var(--radius);
}
.ab-sm-row { display: flex; gap: var(--space-2); margin-top: var(--space-3); justify-content: flex-end; flex-wrap: wrap; }
.ab-sm-go, .ab-sm-cancel {
  min-height: 2.5rem;
  padding: var(--space-2) var(--space-3);
  font: inherit;
  border-radius: var(--radius);
  border: 1px solid var(--read-rule);
  background: transparent;
  color: var(--read-ink);
  cursor: pointer;
}
.ab-sm-go { background: var(--read-link); border-color: var(--read-link); color: var(--ink-inverse); }
.ab-sm-go:disabled, .ab-sm-cancel:disabled { opacity: 0.5; cursor: default; }

/* ── takes ──────────────────────────────────────────────────────────────────── */
.ab-sm-takes { list-style: none; margin: var(--space-2) 0 0; padding: 0; display: flex; flex-direction: column; gap: var(--space-1); }
.ab-sm-take {
  display: grid;
  grid-template-columns: 2.25rem 1fr auto;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  border: 1px solid transparent;
  border-radius: var(--radius);
}
.ab-sm-take[data-active="1"] { border-color: var(--read-rule); background: var(--reader-hover, var(--hover)); }
.ab-sm-take-play {
  grid-row: span 2;
  width: 2.25rem; height: 2.25rem;
  border: 1px solid var(--read-rule);
  border-radius: var(--radius-pill);
  background: transparent;
  color: var(--read-ink);
  font: inherit; cursor: pointer;
}
.ab-sm-take-play:disabled { opacity: 0.4; cursor: default; }
.ab-sm-take-name { font-weight: 600; }
.ab-sm-take-chip {
  margin-inline-start: var(--space-2);
  padding: 0 0.4em;
  font-size: var(--text-2xs, 0.6875rem);
  font-weight: 400;
  color: var(--read-ink-muted);
  border: 1px solid var(--read-rule);
  border-radius: var(--radius-pill);
}
.ab-sm-take-meta { grid-column: 2; color: var(--read-ink-muted); font-size: var(--text-2xs, 0.6875rem); }
.ab-sm-take-use {
  grid-row: span 2;
  min-height: 2.25rem;
  padding: 0 var(--space-3);
  font: inherit;
  border: 1px solid var(--read-rule);
  border-radius: var(--radius);
  background: transparent;
  color: var(--read-ink);
  cursor: pointer;
}
.ab-sm-take-use:disabled { opacity: 0.45; cursor: default; }

/* ── the selection chip ─────────────────────────────────────────────────────── */
.ab-sel-chip {
  position: fixed;
  z-index: 62;
  left: 50%;
  transform: translateX(-50%);
  bottom: calc(env(safe-area-inset-bottom, 0px) + var(--space-4));
  display: flex;
  align-items: center;
  gap: var(--space-2);
  max-width: calc(100vw - 2 * var(--space-3));
  padding: var(--space-2) var(--space-2) var(--space-2) var(--space-3);
  font-family: var(--font-ui);
  font-size: var(--text-sm, 0.875rem);
  color: var(--read-ink);
  background: var(--read-bg);
  border: 1px solid var(--read-rule);
  border-radius: var(--radius-pill);
  box-shadow: var(--shadow-lift);
}
.ab-sel-count { white-space: nowrap; }
.ab-sel-timer { color: var(--read-ink-muted); }
.ab-sel-btn {
  min-height: 2.25rem;
  padding: 0 var(--space-3);
  font: inherit;
  white-space: nowrap;
  border: 1px solid var(--read-rule);
  border-radius: var(--radius-pill);
  background: transparent;
  color: var(--read-ink);
  cursor: pointer;
}
.ab-sel-primary { background: var(--read-link); border-color: var(--read-link); color: var(--ink-inverse); }
`;

export default SelectionChip;
